// M1 throwaway: FE-owned chat lifecycle for case analytics.
//
// Domain-specific provisioning (case analytics system prompt, case-scoped
// tools) lives here. The shared session state machine is in
// useAnalyticsChatSession.

import { useCallback, useMemo } from "react";

import { v4 as uuidv4 } from "uuid";

import {
  type AnalyticsChatResult,
  useAnalyticsChatSession,
} from "src/analytics/copilot/useAnalyticsChatSession";
import {
  CASE_SPECIAL_DOMAINS,
  specialDomainsForAgent,
} from "src/analytics/metrics/VisualizeMetrics/liveAnalyticsDomains";
import { agentManagementApi } from "src/api/endpoints";
import { useCurrentWorkspace } from "src/api/queries";
import { buildCaseAnalyticsSystemPrompt } from "src/cases/analytics/m1/caseAnalyticsSystemPrompt";
import {
  CASE_QUERY_METRICS_TOOL_DESCRIPTION,
  CASE_REPLY_VISUALIZE_METRICS_TOOL_DESCRIPTION,
} from "src/cases/analytics/m1/caseAnalyticsToolDescriptions";
import {
  caseTemplateCatalogUnavailableBlock,
  formatCaseTemplateSchemaBlock,
} from "src/cases/analytics/m1/caseTemplateSchemaSnapshot";
import { useCaseTemplatesRaw } from "src/cases/queries/templates";
import {
  type AgentVersionConfigurationInputToolsInner,
  AgentVersionConfigurationInputToolsInnerTypeEnum,
  Model,
} from "src/clients/agent-management";
import type { CaseTemplatesResponse } from "src/clients/case-management";
import type { Environment } from "src/hooks/useEnvironment";

const SLUG_PREFIX = "cm-analytics-m1-";
const AGENT_NAME = "Case Analytics (M1)";
const AGENT_DESCRIPTION =
  "Auto-provisioned per-session Case Analytics chat agent (M1 throwaway)";

const DATA_SOURCE = "case_analytics";

const SESSIONS_STORAGE_KEY = "caseAnalyticsM1Sessions";

const buildTools = (): AgentVersionConfigurationInputToolsInner[] =>
  [
    {
      id: uuidv4(),
      type: AgentVersionConfigurationInputToolsInnerTypeEnum.QUERY_METRICS,
      name: "query_metrics",
      description: CASE_QUERY_METRICS_TOOL_DESCRIPTION,
      data_source: DATA_SOURCE,
    },
    {
      id: uuidv4(),
      type: AgentVersionConfigurationInputToolsInnerTypeEnum.REPLY_VISUALIZE_METRICS,
      name: "reply_visualize_metrics",
      description: CASE_REPLY_VISUALIZE_METRICS_TOOL_DESCRIPTION,
      chart_format: "vega",
      special_domains: specialDomainsForAgent(CASE_SPECIAL_DOMAINS),
      data_source: DATA_SOURCE,
    },
  ] as unknown as AgentVersionConfigurationInputToolsInner[];

const provisionAgent = async (
  baseUrl: string,
  env: Environment,
  templateSchemaBlock: string,
): Promise<string> => {
  const mgmt = agentManagementApi(baseUrl);

  const systemPrompt = buildCaseAnalyticsSystemPrompt({
    env,
    templateSchemaBlock,
  });

  const slug = SLUG_PREFIX + uuidv4().slice(0, 8);
  const createResp = await mgmt.createAgentCopilotsManagementApiV0AgentsPost({
    agentCreate: {
      slug,
      name: AGENT_NAME,
      description: AGENT_DESCRIPTION,
    },
  });
  const agentId = createResp.data.agent.id;

  const versionResp =
    await mgmt.createVersionCopilotsManagementApiV0AgentsAgentIdVersionsPost({
      agentId,
      agentVersionCreate: {
        name: "v1",
        description: "Auto-provisioned M1 Case Analytics version",
        configuration: {
          model_config: {
            model: Model.CLAUDE_4_5_SONNET,
            system_prompt: systemPrompt,
            temperature: 0,
          },
          tools: buildTools(),
        },
      },
    });
  const versionId = versionResp.data.agent_version.id;

  await mgmt.publishVersionCopilotsManagementApiV0AgentsAgentIdVersionsVersionIdPublishPost(
    {
      agentId,
      versionId,
      ifMatch: "0",
    },
  );

  return agentId;
};

const sessionSlotKey = (
  workspaceId: string,
  dashboardId: string | undefined,
  env: Environment,
): string =>
  dashboardId
    ? `${workspaceId}:${dashboardId}:${env}`
    : `${workspaceId}:${env}`;

export const useCaseAnalyticsChat = ({
  env,
  dashboardId,
}: {
  env: Environment;
  dashboardId: string | undefined;
}): AnalyticsChatResult => {
  const { data: workspace } = useCurrentWorkspace();
  const workspaceId = workspace?.id ?? null;

  const {
    data: templatesResponse,
    isSuccess: templatesLoaded,
    isError: templatesFailed,
    error: templatesError,
  } = useCaseTemplatesRaw<CaseTemplatesResponse>();

  const templateSchemaBlock = useMemo(() => {
    if (templatesResponse) {
      return formatCaseTemplateSchemaBlock(templatesResponse);
    }
    if (templatesFailed) {
      const msg =
        templatesError instanceof Error
          ? templatesError.message
          : String(templatesError);
      return caseTemplateCatalogUnavailableBlock(msg);
    }
    return caseTemplateCatalogUnavailableBlock("templates not loaded yet");
  }, [templatesResponse, templatesFailed, templatesError]);

  const templatesReady = templatesLoaded || templatesFailed;

  const provision = useCallback(
    (baseUrl: string) => provisionAgent(baseUrl, env, templateSchemaBlock),
    [env, templateSchemaBlock],
  );

  return useAnalyticsChatSession({
    storageKey: SESSIONS_STORAGE_KEY,
    sessionSlotKey: workspaceId
      ? sessionSlotKey(workspaceId, dashboardId, env)
      : null,
    environment: env,
    provision,
    provisionReady: templatesReady,
  });
};
